import React, {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import firebase, {auth, signOut} from "../firebase";
import {IDLE_MS, sessionKeys, activityTime, idleExpired, resumeAuthenticatedSession} from "../lib/sessionLock.mjs";
import {navigate} from "../history";
import "./sessionLock.css";

// A privacy lock, not a replacement for Firebase authorization or server rules.
const events = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
export const INACTIVITY_TIMEOUT_MS = IDLE_MS;
const timeoutMinutes = INACTIVITY_TIMEOUT_MS / 60000;
const read = key => {try {return localStorage.getItem(key);} catch {return null;}};
const write = (key, value) => {try {localStorage.setItem(key, String(value));} catch { /* Local lock still works if storage is unavailable. */ }};
export default function AppLogout({children, enabled}) {
  const {pathname} = useLocation();
  // Public pages stay accessible while the authenticated portal remains locked.
  const protectedPage = /^\/(operation|messaging|report|admin|user|planning|planning_chi)(\/|$)/.test(pathname);
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lockRef = useRef(false);
  const last = useRef(Date.now());
  const broadcast = useRef(0);
  const keysRef = useRef(null);
  const uid = auth.currentUser?.uid;
  useEffect(() => {
    if (!enabled || !uid) {lockRef.current = false;setLocked(false);return;}
    const keys = sessionKeys(uid);keysRef.current = keys;
    lockRef.current = false;setLocked(false);setPassword("");setError("");
    const now = Date.now();
    last.current = activityTime(read(keys.activity), now) || now;
    if (!read(keys.activity)) write(keys.activity, last.current);
    const lock = () => {if (lockRef.current) return;lockRef.current = true;setLocked(true);setPassword("");write(keys.locked, "1");};
    const check = () => {
      const shared = activityTime(read(keys.activity), Date.now());
      if (shared) last.current = Math.max(last.current, shared);
      if (read(keys.locked) === "1" || idleExpired(last.current, Date.now(), INACTIVITY_TIMEOUT_MS)) lock();
    };
    check();
    const activity = () => {
      // Check elapsed time before accepting the first event after sleep.
      check();if (lockRef.current) return;
      last.current = Date.now();
      if (Date.now() - broadcast.current >= 5000) {broadcast.current = Date.now();write(keys.activity, last.current);}
    };
    const storage = event => {
      if (event.key === keys.locked && event.newValue === "1") {lockRef.current = true;setLocked(true);setPassword("");}
      if (event.key === keys.unlock && read(keys.locked) === "0") {
        last.current = Date.now();lockRef.current = false;setLocked(false);setPassword("");setError("");
      }
    };
    const authenticated = event => {
      if (event.detail?.uid !== uid) return;
      last.current = event.detail.at;lockRef.current = false;setLocked(false);setPassword("");setError("");
    };
    window.addEventListener("wws:session-authenticated", authenticated);
    const timer = window.setInterval(check, 1000);
    events.forEach(name => window.addEventListener(name, activity, {passive:true}));
    window.addEventListener("storage", storage);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {window.removeEventListener("wws:session-authenticated", authenticated);clearInterval(timer);events.forEach(name => window.removeEventListener(name, activity));window.removeEventListener("storage", storage);window.removeEventListener("focus", check);document.removeEventListener("visibilitychange", check);};
  }, [enabled, uid]);
  useEffect(() => {if (locked && protectedPage) document.getElementById("session-unlock-password")?.focus();}, [locked, protectedPage]);
  const unlock = async event => {
    event.preventDefault();if (busy) return;setBusy(true);setError("");
    const user = auth.currentUser;const keys = keysRef.current;
    try {
      if (!user?.email || user.uid !== uid || !keys) throw new Error("session-changed");
      await user.reauthenticateWithCredential(firebase.auth.EmailAuthProvider.credential(user.email, password));
      if (auth.currentUser?.uid !== user.uid) throw new Error("session-changed");
      last.current = resumeAuthenticatedSession(user.uid, {setItem: (key, value) => window.localStorage.setItem(key, value)});
      lockRef.current = false;setLocked(false);setPassword("");
    } catch (failure) {
      setPassword("");
      setError(failure.code === "auth/too-many-requests" ? "Too many attempts. Please wait before trying again." : failure.code === "auth/network-request-failed" ? "Cannot connect. Check your connection and try again." : "Unable to unlock. Check your password or sign out and sign in again.");
    } finally {setBusy(false);}
  };
  const showLock = locked && protectedPage;
  return <>
    <div hidden={showLock} inert={showLock ? true : undefined} aria-hidden={showLock ? true : undefined}>{children}</div>
    {showLock && <div className="sessionLock" role="dialog" aria-modal="true" aria-labelledby="session-lock-title">
      <form className="sessionLockCard" onSubmit={unlock}>
        <div className="sessionLockMark" aria-hidden="true">&#128274;</div>
        <h1 id="session-lock-title">Session locked</h1>
        <p>Your session paused after {timeoutMinutes} minutes of inactivity. Enter your password to continue.</p>
        <p className="sessionLockEmail">{auth.currentUser?.email}</p>
        <label htmlFor="session-unlock-password">Password</label>
        <input id="session-unlock-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required disabled={busy}/>
        {error && <p role="alert" className="sessionLockError">{error}</p>}
        <button type="submit" disabled={busy || !password}>{busy ? "Verifying…" : "Unlock session"}</button>
        <button className="sessionLockSignOut" type="button" disabled={busy} onClick={async () => {setBusy(true);try {await signOut();navigate("/");} catch {setError("Unable to sign out. Please try again.");setBusy(false);}}}>Sign out / Switch account</button>
        <small>Your unsaved work stays in this tab. Newer server changes may require a refresh before saving.</small>
      </form>
    </div>}
  </>;
}
